/**
 * API client functions for the Data Discovery service
 * All endpoints proxy through /api/discovery -> data-discovery-api:3003
 */

import { get, post, put, del } from '@/lib/api-client'
import type { PaginatedResponse } from '@/types/api'
import type {
  DataConnection,
  CreateConnectionRequest,
  UpdateConnectionRequest,
  ConnectionQueryParams,
  TestConnectionResult,
  DiscoveryScan,
  CreateScanRequest,
  ScanQueryParams,
  StartScanResponse,
  DiscoveryResult,
  ScanResultsQueryParams,
  SchemaInfo,
  TableInfo,
  ColumnInfo,
} from '@/types/discovery'

// ============================================================================
// CONNECTIONS
// ============================================================================

function buildQueryString(
  params: Record<string, string | number | boolean | undefined | null>
): string {
  const qs = new URLSearchParams()
  for (const [key, value] of Object.entries(params)) {
    if (value !== undefined && value !== null) {
      qs.set(key, String(value))
    }
  }
  const str = qs.toString()
  return str ? `?${str}` : ''
}

function encId(id: string): string {
  return encodeURIComponent(id)
}

export function fetchConnections(
  params: ConnectionQueryParams = {}
): Promise<PaginatedResponse<DataConnection>> {
  const qs = buildQueryString(params as Record<string, string | number | boolean | undefined | null>)
  return get<PaginatedResponse<DataConnection>>(`/discovery/connections${qs}`)
}

export function fetchConnection(id: string): Promise<DataConnection> {
  return get<DataConnection>(`/discovery/connections/${encId(id)}`)
}

export function createConnection(
  data: CreateConnectionRequest
): Promise<DataConnection> {
  return post<DataConnection, CreateConnectionRequest>(
    '/discovery/connections',
    data
  )
}

export function updateConnection(
  id: string,
  data: UpdateConnectionRequest
): Promise<DataConnection> {
  return put<DataConnection, UpdateConnectionRequest>(
    `/discovery/connections/${encId(id)}`,
    data
  )
}

export function deleteConnection(id: string): Promise<void> {
  return del<void>(`/discovery/connections/${encId(id)}`)
}

export function testConnection(id: string): Promise<TestConnectionResult> {
  return post<TestConnectionResult>(`/discovery/connections/${encId(id)}/test`)
}

// ============================================================================
// SCANS
// ============================================================================

export function fetchScans(
  params: ScanQueryParams = {}
): Promise<PaginatedResponse<DiscoveryScan>> {
  const qs = buildQueryString(params as Record<string, string | number | boolean | undefined | null>)
  return get<PaginatedResponse<DiscoveryScan>>(`/discovery/scans${qs}`)
}

export function fetchScan(id: string): Promise<DiscoveryScan> {
  return get<DiscoveryScan>(`/discovery/scans/${encId(id)}`)
}

export function startScan(data: CreateScanRequest): Promise<StartScanResponse> {
  return post<StartScanResponse, CreateScanRequest>('/discovery/scans', data)
}

export function fetchScanResults(
  scanId: string,
  params: ScanResultsQueryParams = {}
): Promise<PaginatedResponse<DiscoveryResult>> {
  const qs = buildQueryString(params as Record<string, string | number | boolean | undefined | null>)
  return get<PaginatedResponse<DiscoveryResult>>(
    `/discovery/scans/${encId(scanId)}/results${qs}`
  )
}

export function reclassifyScan(
  scanId: string,
  options: { resultIds?: readonly string[]; enableAiClassification?: boolean }
): Promise<{ message: string }> {
  return post<{ message: string }>(`/discovery/scans/${encId(scanId)}/reclassify`, options)
}

export function deleteScan(id: string): Promise<{ deleted: boolean }> {
  return del<{ deleted: boolean }>(`/discovery/scans/${encId(id)}`)
}

// ============================================================================
// SOURCE INTROSPECTION
// ============================================================================

export function fetchSchemas(connectionId: string): Promise<SchemaInfo[]> {
  return get<SchemaInfo[]>(`/discovery/connections/${encId(connectionId)}/schemas`)
}

export function fetchTables(
  connectionId: string,
  schemaName: string
): Promise<TableInfo[]> {
  return get<TableInfo[]>(
    `/discovery/connections/${encId(connectionId)}/schemas/${encodeURIComponent(schemaName)}/tables`
  )
}

export function fetchColumns(
  connectionId: string,
  schemaName: string,
  tableName: string
): Promise<ColumnInfo[]> {
  return get<ColumnInfo[]>(
    `/discovery/connections/${encId(connectionId)}/schemas/${encodeURIComponent(schemaName)}/tables/${encodeURIComponent(tableName)}/columns`
  )
}
